import { useBase, useRecords } from '@airtable/blocks/interface/ui';
import { TABLE } from '../ids';
import type { UnitRow } from './buildWorkbook';
import { stripEmoji, bucketFromStatus } from './salesBucket';

/** Units-table fields the workbook needs (verified live in base apptHlTm4M43b35IA). */
const UF = {
  unitName: 'fldFUabBCRIY3qhxx',
  lotsLink: 'fldKt4u4ru8VZqLLG',
  lotNo: 'fldpVQmWRkSyJP3bC',       // Lot # (from Lots)
  block: 'fldKon5iFP6WlEGeH',       // Block # (from Lots)
  phase: 'fldFxCtF16LG3BlFz',
  subPhase: 'fldenEbjPSpsXDtMT',
  concurrency: 'fldCeTpht3QiSCM4C',
  landUse: 'fldRk8Lbz46JFOhgx',     // Category (from Land Use)
  model: 'fld8StZBj61U4ZAb0',
  pkg: 'fldQvSbo6FeMcHzrm',         // Unit Package
  version: 'fld6jI6nuKiy7nv0L',     // Unit Model Version → feeds the 'House Type Version' export column (was Succo Version fldNlLgdwjGaFhKAQ, a stucco-finish rev — wrong field)
  orientation: 'fldR8ncFMicLLnQL6',
  lotSF: 'fldwXPPLZ70Bee1gL',       // Square Feet (Lot)
  lakeview: 'fld9xUrIMrtpyrClP',
  sidewalk: 'fldzhgh3BBsBaGPn3',
  contractStatus: 'fldY2EZi53EsJJj3W',
  contractStatusLive: 'fldoMislCULskpKGm', // "Contract Status (Live)" — live GHL else synced; the map binds here
  buyer: 'fldOTVlmCVPxWKXGM',
  value: 'fld6p0EsXYTy3LbH8',
  buildStageV5: 'fldFusnkDq5trbe0g',       // Build Stage (v5) — current construction step
  percentCompleteV5: 'fldf11yCGghBzT1Rf',  // Percent Complete (v5) — 0..1 fraction
  permitUrl: 'fldE7BMZuGTk8mGk9',   // Permit URL (from Permits) (from Lots)
} as const;

// Guarded reads — interface extensions expose a field-limited view of a bound table, so a
// withheld field must degrade gracefully instead of throwing during export.
const cv = (r: any, f: string): any => { try { return r ? r.getCellValue(f) : null; } catch { return null; } };
const str = (r: any, f: string): string => { try { return r ? (r.getCellValueAsString(f) || '') : ''; } catch { return ''; } };
const linkIds = (r: any, f: string): string[] => { const v = cv(r, f); return Array.isArray(v) ? v.map((x: any) => x.id) : []; };
const numFrom = (s: string): number | null => { const m = (s || '').replace(/[, ]/g, '').match(/-?\d+(\.\d+)?/); return m ? parseFloat(m[0]) : null; };
const lookupBool = (r: any, f: string): boolean => { const v = cv(r, f); if (Array.isArray(v)) return v.some(Boolean); return !!v; };

export function useExportData(): { ready: boolean; buildRows: (visibleLotIds: Set<string>) => UnitRow[] } {
  const base = useBase();
  const unitsT = base.getTableByIdIfExists(TABLE.units) || null;
  // Only request fields the bound table exposes (interface data sources may withhold some).
  const fields = (Object.values(UF) as string[]).filter(id => {
    try { return !!(unitsT && (unitsT as any).getFieldByIdIfExists && (unitsT as any).getFieldByIdIfExists(id)); } catch { return false; }
  });
  const units = useRecords(unitsT as any, { fields });

  function buildRows(visibleLotIds: Set<string>): UnitRow[] {
    if (!units) return [];
    const out: UnitRow[] = [];
    for (const u of units) {
      const lotIds = linkIds(u, UF.lotsLink);
      if (!lotIds.some(id => visibleLotIds.has(id))) continue;

      const unitName = str(u, UF.unitName);
      const lotPart = (unitName.split('-')[0] || '').trim();
      const lotNo = numFrom(str(u, UF.lotNo));
      const block = str(u, UF.block).split(',')[0].trim();
      // Match the map: read "Contract Status (Live)" (live GHL stage when a buyer is linked, else the
      // synced Contract Status) so a filtered export's Sales Status agrees with the on-screen lens /
      // filter. Fall back to the synced field if the live formula is withheld from this data source.
      const rawStatus = str(u, UF.contractStatusLive) || str(u, UF.contractStatus);
      const salesStatus = stripEmoji(rawStatus) || 'No Sales Status';
      const bucket = bucketFromStatus(rawStatus);
      const url = str(u, UF.permitUrl).split(',')[0].trim();
      const linked = url ? 'Yes' : 'No';

      out.push({
        lotDisplay: lotPart ? `Lot ${lotPart}` : (unitName || ''),
        unit: unitName,
        lotNo,
        blockLot: (block && lotNo != null) ? `Block ${block} / Lot ${lotNo}` : str(u, UF.lotNo),
        phase: str(u, UF.phase), subPhase: str(u, UF.subPhase), concurrency: str(u, UF.concurrency),
        landUse: str(u, UF.landUse), model: str(u, UF.model),
        pkg: str(u, UF.pkg) || 'Standard', version: str(u, UF.version), orientation: str(u, UF.orientation),
        lotSF: numFrom(str(u, UF.lotSF)),
        lakeview: lookupBool(u, UF.lakeview) ? 'Yes' : 'No',
        sidewalk: lookupBool(u, UF.sidewalk) ? 'Yes' : 'No',
        // GHL package tier + physical construction upgrades live on the sale / GHL Inbox, not the
        // Units record — the caller (SiteMapApp.exportXLSX) joins them in by lot. (Was: upgradePkg
        // duplicated Unit Package.)
        packageSelection: '', constructionUpgrades: '',
        // GHL incentive/escrow fields are joined in by the caller (SiteMapApp.exportXLSX) from the
        // lot's parcel/sale — blank placeholders here, same as constructionUpgrades above.
        financialIncentives: '', jobContingency: '', additionalContingencies: '',
        salePrice: null, depositUponSigning: null, contractExecutionDate: '',
        escrowStatus: '', totalDepositedInEscrow: null, finalSalesPrice: null,
        salesStatus, salesBucket: bucket, committed: bucket === 'Sold / Committed' ? 'Yes' : 'No',
        buyer: str(u, UF.buyer) || 'No buyer',
        value: numFrom(str(u, UF.value)),
        // Build Stage (v5) + duration-weighted Build % Complete — read the raw 0..1 fraction (not the
        // "%"-formatted string) so the workbook's PCT cell renders it correctly; null when untracked.
        buildStage: str(u, UF.buildStageV5),
        percentComplete: (() => { const v = cv(u, UF.percentCompleteV5); return typeof v === 'number' ? v : null; })(),
        // permitNum/amountDue are permit-grain — the caller (SiteMapApp.exportXLSX) joins the REAL
        // linked permit number(s) + fee balance by lot. Leave neutral here; permitLinked stays the
        // honest Yes/No derived from the lot's Permit URL lookup. (Previously these were fabricated:
        // permitNum = "Linked permit"/"No Permit", amountDue = 0/null — never the real values.)
        permitLinked: linked, permitNum: '',
        permitUrl: url, amountDue: null,
        lotIds,
      });
    }
    // de-dupe lot rows count is handled by the workbook engine; keep all unit rows
    return out;
  }

  return { ready: !!units, buildRows };
}
